import { useState } from 'react';
import { flushSync } from 'react-dom';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { s } from '@/theme';
import { r } from '@/theme/tokens/radii';
import {
  AddIcon,
  Box,
  ChevronRightIcon,
  Divider,
  KeyboardDoubleArrowLeftIcon,
  KeyboardDoubleArrowRightIcon,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Text,
  Tooltip,
} from '@/ui/primitives';
import { Stack } from '../layout/Stack';
import type { ReactElement, ReactNode } from 'react';

export type SidebarItem = {
  id: string;
  label: string;
  href: string;
  icon?: ReactElement;
  leadingAdornment?: ReactElement;
  active?: boolean;
  disabled?: boolean;
  disabledTone?: 'muted' | 'default';
  preload?: () => Promise<void> | null;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  popover?: {
    title?: string;
    items: Array<{
      id: string;
      label: string;
      onClick?: () => void;
      leadingAdornment?: ReactElement;
      helperText?: string;
    }>;
  };
  rightAdornment?: ReactElement;
};

export type SidebarSection = { title: string; items: SidebarItem[] };

type SidebarNavProps = {
  sections: SidebarSection[];
  preFooterItems?: SidebarItem[];
  footerItems?: SidebarItem[];
  footerTitle?: string;
  footerContent?: ReactNode;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  onBeforeNavigate?: (href: string, label: string) => void;
};

export const SidebarNav = ({
  sections,
  preFooterItems,
  footerItems,
  footerTitle,
  footerContent,
  collapsed = false,
  onToggleCollapsed,
  onBeforeNavigate,
}: SidebarNavProps): ReactElement => {
  const navigate = useNavigate();
  const sidebarItemMinHeight = 40;
  const collapsedSelectedSidebarItemSize = 32;
  const sidebarItemFontSize = '14px';
  const sidebarItemFontWeight = 500;
  const selectedSidebarItemFontWeight = 600;
  const sidebarSectionLabelFontSize = '13px';
  const sidebarSectionLabelBottomSpacing = s('md');
  const leadingAdornmentSpacing = `calc(${s('sm')} + ${s('md')})`;
  const lightSidebarItemColour = 'rgba(31, 45, 65, 0.72)';
  const darkSidebarItemColour = 'rgba(244, 247, 251, 0.72)';
  const lightSidebarIconColour = 'rgba(31, 45, 65, 0.64)';
  const darkSidebarIconColour = 'rgba(244, 247, 251, 0.68)';
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activePopover, setActivePopover] = useState<SidebarItem['popover'] | null>(null);
  const resolvedPreFooterItems = preFooterItems ?? [];
  const resolvedFooterItems = footerItems ?? [];
  const hasPreFooterItems = resolvedPreFooterItems.length > 0;
  const hasFooterItems = resolvedFooterItems.length > 0;

  const scheduleNavigationAfterLoadingPaint = (href: string): void => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        void navigate(href);
      });
    });
  };

  const handleClose = (): void => {
    setAnchorEl(null);
    setActivePopover(null);
  };

  const renderSidebarItem = (item: SidebarItem): ReactElement => {
    const itemNode = (
      <Box
        key={item.id}
        component={item.popover ? 'a' : RouterLink}
        {...(item.popover ? { href: item.href } : { to: item.href })}
        aria-disabled={item.disabled ? true : undefined}
        data-has-popover={item.popover ? 'true' : undefined}
        onMouseEnter={() => {
          if (!item.disabled) {
            void item.preload?.();
          }
        }}
        onFocus={() => {
          if (!item.disabled) {
            void item.preload?.();
          }
        }}
        onMouseDown={(event) => {
          if (item.disabled || item.active || item.popover || !item.href.startsWith('/')) {
            return;
          }
          const isPrimaryButton = event.button === 0;
          const isModifiedClick = event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
          if (!isPrimaryButton || isModifiedClick) {
            return;
          }
          flushSync(() => {
            onBeforeNavigate?.(item.href, item.label);
          });
        }}
        onClick={(event) => {
          if (item.disabled) {
            event.preventDefault();
            return;
          }
          if (!item.popover && item.href.startsWith('/')) {
            const isPrimaryButton = event.button === 0;
            const isModifiedClick =
              event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
            if (isPrimaryButton && !isModifiedClick) {
              event.preventDefault();
              flushSync(() => {
                onBeforeNavigate?.(item.href, item.label);
              });
              void item.preload?.()?.catch(() => null);
              scheduleNavigationAfterLoadingPaint(item.href);
              return;
            }
          }
          item.onClick?.(event);
          if (item.popover) {
            event.preventDefault();
            setAnchorEl(event.currentTarget);
            setActivePopover(item.popover);
          }
        }}
        aria-current={item.active ? 'page' : undefined}
        sx={(theme) => ({
          width: '100%',
          maxWidth: collapsed ? sidebarItemMinHeight : 'none',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          textAlign: 'left',
          overflow: 'hidden',
          position: 'relative',
          paddingX: collapsed ? s('none') : s('lg'),
          paddingY: s('xs'),
          borderRadius: collapsed ? r('pill') : r('sm'),
          textDecoration: 'none',
          color: item.active
            ? theme.palette.common.white
            : item.disabled && item.disabledTone !== 'default'
              ? theme.palette.text.secondary
              : theme.palette.mode === 'dark'
                ? darkSidebarItemColour
                : lightSidebarItemColour,
          fontWeight: item.active ? selectedSidebarItemFontWeight : sidebarItemFontWeight,
          fontSize: sidebarItemFontSize,
          lineHeight: theme.typography.body.lineHeight,
          minHeight: sidebarItemMinHeight,
          backgroundColor: item.active && !collapsed ? theme.app.colors.primary : 'transparent',
          cursor: item.disabled ? 'default' : 'pointer',
          pointerEvents: item.disabled ? 'none' : 'auto',
          transition: 'background-color 120ms ease, color 120ms ease',
          transitionProperty: 'background-color, color, padding, justify-content',
          transitionDuration: '180ms',
          transitionTimingFunction: 'ease',
          '&:hover': item.active
            ? undefined
            : {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.05)',
              },
          '&:focus-visible': {
            outline: `2px solid ${theme.app.state.focus}`,
            outlineOffset: 2,
          },
          '&::before':
            item.active && collapsed
              ? {
                  content: '""',
                  position: 'absolute',
                  inset: '50% auto auto 50%',
                  width: collapsedSelectedSidebarItemSize,
                  height: collapsedSelectedSidebarItemSize,
                  borderRadius: r('pill'),
                  backgroundColor: theme.app.colors.primary,
                  transform: 'translate(-50%, -50%)',
                }
              : undefined,
          '& > *': {
            position: 'relative',
            zIndex: 1,
          },
        })}
      >
        {item.leadingAdornment && !collapsed ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              mr: leadingAdornmentSpacing,
            }}
          >
            {item.leadingAdornment}
          </Box>
        ) : null}
        {item.icon ? (
          <ListItemIcon
            sx={(theme) => ({
              minWidth: collapsed ? '100% !important' : '20px !important',
              width: collapsed ? '100%' : 20,
              mr: collapsed ? s('none') : s('md'),
              color: item.active
                ? theme.palette.common.white
                : item.disabled && item.disabledTone !== 'default'
                  ? theme.palette.text.secondary
                  : theme.palette.mode === 'dark'
                    ? darkSidebarIconColour
                    : lightSidebarIconColour,
              flexShrink: 0,
              opacity: item.active ? 0.92 : 0.8,
              justifyContent: 'center',
              '& .MuiSvgIcon-root': {
                fontSize: '21px',
                stroke: 'currentColor',
                strokeWidth: 0.08,
              },
            })}
          >
            {item.icon}
          </ListItemIcon>
        ) : null}
        {collapsed ? null : (
          <Text
            role="body"
            component="span"
            sx={{
              minWidth: 0,
              flex: '1 1 auto',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'inherit',
              fontWeight: item.active ? selectedSidebarItemFontWeight : sidebarItemFontWeight,
              opacity: 1,
              transform: 'translateX(0)',
              transition: 'opacity 120ms ease, transform 180ms ease',
            }}
          >
            {item.label}
          </Text>
        )}
        {!collapsed && (item.popover || item.rightAdornment) ? (
          <Box
            sx={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: s('xs'),
              flexShrink: 0,
            }}
          >
            {item.rightAdornment}
            {item.popover ? <ChevronRightIcon fontSize="small" /> : null}
          </Box>
        ) : null}
      </Box>
    );

    return collapsed ? (
      <Tooltip key={item.id} title={item.label} placement="right" arrow>
        {itemNode}
      </Tooltip>
    ) : (
      itemNode
    );
  };

  const renderCollapseControl = (): ReactElement | null => {
    if (!onToggleCollapsed) return null;
    const label = collapsed ? 'Expand Sidebar' : 'Collapse Sidebar';
    const icon = collapsed ? <KeyboardDoubleArrowRightIcon /> : <KeyboardDoubleArrowLeftIcon />;
    const control = (
      <Box
        component="button"
        type="button"
        aria-label={label}
        onClick={onToggleCollapsed}
        sx={(theme) => ({
          width: '100%',
          maxWidth: collapsed ? sidebarItemMinHeight : 'none',
          boxSizing: 'border-box',
          minHeight: sidebarItemMinHeight,
          border: 'none',
          borderRadius: collapsed ? r('pill') : r('sm'),
          backgroundColor: 'transparent',
          color: theme.palette.mode === 'dark' ? darkSidebarIconColour : lightSidebarIconColour,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          overflow: 'hidden',
          px: collapsed ? s('none') : s('lg'),
          py: s('xs'),
          font: 'inherit',
          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.app.state.focus}`,
            outlineOffset: 2,
          },
        })}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? '100% !important' : '20px !important',
            width: collapsed ? '100%' : 20,
            mr: collapsed ? s('none') : s('md'),
            color: 'inherit',
            justifyContent: 'center',
            '& .MuiSvgIcon-root': {
              fontSize: '21px',
              stroke: 'currentColor',
              strokeWidth: 0.08,
            },
          }}
        >
          {icon}
        </ListItemIcon>
        {collapsed ? null : (
          <Text
            role="body"
            component="span"
            sx={{
              color: (theme) =>
                theme.palette.mode === 'dark' ? darkSidebarItemColour : lightSidebarItemColour,
              fontWeight: sidebarItemFontWeight,
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Collapse
          </Text>
        )}
      </Box>
    );
    return collapsed ? (
      <Tooltip title={label} placement="right" arrow>
        {control}
      </Tooltip>
    ) : (
      control
    );
  };

  return (
    <Box
      component="nav"
      aria-label="Primary"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minWidth: 0,
        overflowX: 'hidden',
      }}
    >
      {sections.map((section, index) => (
        <Box
          key={`${section.title}-${String(index)}`}
          sx={{ marginTop: index === 0 ? s('none') : s('sm') }}
        >
          {section.title && !collapsed ? (
            <Text
              role="label"
              component="div"
              sx={{
                color: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(244, 247, 251, 0.56)' : 'text.secondary',
                display: 'block',
                paddingBottom: sidebarSectionLabelBottomSpacing,
                paddingX: s('md'),
                fontSize: sidebarSectionLabelFontSize,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                opacity: 1,
                transition: 'opacity 120ms ease',
              }}
            >
              {section.title}
            </Text>
          ) : null}
          <Stack
            gap="sm"
            sx={{ alignItems: collapsed ? 'center' : 'stretch', marginBottom: s('sm') }}
          >
            {section.items.map(renderSidebarItem)}
          </Stack>
        </Box>
      ))}
      {footerTitle && !collapsed ? (
        <Text
          role="label"
          sx={{
            color: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(244, 247, 251, 0.56)' : 'text.secondary',
            marginTop: s('xl'),
            marginBottom: s('sm'),
            fontSize: sidebarSectionLabelFontSize,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {footerTitle}
        </Text>
      ) : null}
      {footerContent}
      <Box sx={{ marginTop: 'auto', paddingTop: s('2xl') }}>
        {hasFooterItems ? (
          <Stack
            gap="sm"
            sx={{
              alignItems: collapsed ? 'center' : 'stretch',
              marginBottom: hasPreFooterItems ? s('md') : s('sm'),
            }}
          >
            {collapsed ? null : (
              <Text
                role="label"
                component="div"
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(244, 247, 251, 0.56)' : 'text.secondary',
                  paddingX: s('md'),
                  paddingBottom: sidebarSectionLabelBottomSpacing,
                  fontSize: sidebarSectionLabelFontSize,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Settings
              </Text>
            )}
            {resolvedFooterItems.map(renderSidebarItem)}
          </Stack>
        ) : null}
        {hasPreFooterItems ? (
          <Stack
            gap="sm"
            sx={{ alignItems: collapsed ? 'center' : 'stretch', marginBottom: s('sm') }}
          >
            {hasFooterItems ? (
              <Box sx={{ marginBottom: s('sm') }}>
                <Divider />
              </Box>
            ) : null}
            {resolvedPreFooterItems.map(renderSidebarItem)}
            {renderCollapseControl()}
          </Stack>
        ) : (
          <Stack
            gap="sm"
            sx={{ alignItems: collapsed ? 'center' : 'stretch', marginBottom: s('sm') }}
          >
            {renderCollapseControl()}
          </Stack>
        )}
      </Box>
      <Popover
        open={Boolean(anchorEl && activePopover)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        paperSx={(theme) => ({
          marginLeft: s('sm'),
          width: 'fit-content',
          minWidth: 140,
          maxWidth: 'calc(100vw - 24px)',
          border: `1px solid ${theme.app.colors.border}`,
          borderRadius: `${r('card')} !important`,
          backgroundColor: theme.app.colors.surface,
          backgroundImage: 'none',
          boxShadow: 'none',
          '--Paper-overlay': 'none',
          overflow: 'hidden',
          '& .MuiMenuItem-root': {
            fontSize: sidebarItemFontSize,
            lineHeight: 1.2,
          },
          '& .MuiMenuItem-root .MuiListItemText-primary': {
            fontSize: sidebarItemFontSize,
            lineHeight: 1.2,
          },
        })}
      >
        {(() => {
          const addActionItem =
            activePopover?.items.find((item) => item.id.startsWith('add-')) ?? null;
          const selectableItems =
            activePopover?.items.filter((item) => !item.id.startsWith('add-')) ?? [];
          const allProjectsItem = selectableItems.find((item) => item.id === 'all-projects');
          const otherSelectableItems = selectableItems.filter((item) => item.id !== 'all-projects');
          const orderedSelectableItems = allProjectsItem
            ? [allProjectsItem, ...otherSelectableItems]
            : otherSelectableItems;

          return (
            <>
              {activePopover?.title ? (
                <>
                  <Box sx={{ px: s('lg'), pt: s('md'), pb: s('md') }}>
                    <Text role="body" sx={{ fontSize: sidebarItemFontSize, fontWeight: 600 }}>
                      {activePopover.title}
                    </Text>
                  </Box>
                  <Divider />
                </>
              ) : null}
              <MenuList dense disablePadding sx={{ pt: s('none'), pb: s('none') }}>
                {orderedSelectableItems.map((popoverItem) => (
                  <MenuItem
                    key={popoverItem.id}
                    onClick={() => {
                      popoverItem.onClick?.();
                      handleClose();
                    }}
                    sx={(theme) => ({
                      px: s('lg'),
                      py: s('md'),
                      borderRadius: r('none'),
                      typography: 'body',
                      lineHeight: 1.2,
                      color: theme.app.colors.text,
                      gap: 0,
                    })}
                  >
                    {popoverItem.leadingAdornment ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                          mr: leadingAdornmentSpacing,
                        }}
                      >
                        {popoverItem.leadingAdornment}
                      </Box>
                    ) : null}
                    <Box sx={{ minWidth: 0 }}>
                      <Text
                        role="body"
                        sx={{
                          display: 'block',
                          fontSize: sidebarItemFontSize,
                          fontWeight: sidebarItemFontWeight,
                          lineHeight: 1.2,
                        }}
                      >
                        {popoverItem.label}
                      </Text>
                      {popoverItem.helperText ? (
                        <Text
                          role="caption"
                          color="textSecondary"
                          sx={{ display: 'block', mt: s('xxs') }}
                        >
                          {popoverItem.helperText}
                        </Text>
                      ) : null}
                    </Box>
                  </MenuItem>
                ))}
                {addActionItem ? (
                  <Tooltip
                    title={addActionItem.onClick ? '' : 'Project Limit Reached'}
                    placement="right"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: { fontSize: (theme) => theme.typography.body.fontSize },
                      },
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        if (!addActionItem.onClick) return;
                        addActionItem.onClick();
                        handleClose();
                      }}
                      sx={(theme) => ({
                        px: s('lg'),
                        py: s('md'),
                        borderRadius: r('none'),
                        display: 'flex',
                        alignItems: 'center',
                        gap: s('sm'),
                        color: addActionItem.onClick
                          ? theme.app.colors.text
                          : theme.palette.text.secondary,
                        cursor: addActionItem.onClick ? 'pointer' : 'default',
                        opacity: addActionItem.onClick ? 1 : 0.7,
                        '& .MuiListItemIcon-root': {
                          minWidth: '20px !important',
                          width: 20,
                          mr: 0,
                          color: 'inherit',
                          flexShrink: 0,
                        },
                        '& .MuiListItemText-root': { m: 0 },
                        '& .MuiListItemText-primary': { lineHeight: 1.2 },
                      })}
                    >
                      <ListItemIcon>
                        <AddIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={addActionItem.label} />
                    </MenuItem>
                  </Tooltip>
                ) : null}
              </MenuList>
            </>
          );
        })()}
      </Popover>
    </Box>
  );
};
